// The service worker's cache names, defined once (#1003).
//
// Two very different consumers need these strings:
//
//   - `public/sw.js`, which owns both caches — it fills them, evicts them on
//     activate, and serves the API one when the network fails; and
//   - the app, which purges the API cache on every transition that changes
//     whose data this browser is entitled to see (`lib/offlineCache.ts`).
//
// A name that drifts between the two is a silent data-leak bug rather than a
// build error: the app deletes a cache nobody writes to while the worker keeps
// serving the one that still holds the previous household's responses. So the
// names live here and nowhere else. The worker is a *classic* service worker
// and cannot import this module, so `vite.config.ts` emits the classic-script
// form below at `/sw-cache-names.js` and `sw.js` `importScripts()` it.
export const SW_CACHE_NAMES = {
  /** Versioned app shell (index.html plus the hashed assets it references). */
  shell: "household-shell-v5",
  /**
   * Stale-while-revalidate cache for idempotent `GET /api` reads (#500).
   *
   * Rolled to v2 for the reset protocol (codex review). During the deploy a
   * client is still controlled by the previous worker; the app pulls the new
   * one in to take over, but the old worker's already-running fetch handlers
   * keep going and can still write when they finally resolve. Sharing a cache
   * name with them means one of those stragglers can re-create the previous
   * household's entry after the new worker has reset. Under a new name they
   * write somewhere nothing reads, and the new worker's `activate` cleanup
   * deletes the old cache outright. The cost is one cold offline period per
   * client on this deploy; the shell cache is untouched, so the app still boots
   * offline.
   */
  api: "household-api-v2"
} as const;

/**
 * What every API cache this app has ever written is named with (codex review).
 *
 * The rename above is what stops an old worker's stragglers from polluting the
 * live cache — but it also means the *previous* name still exists and the old
 * worker is still reading it. That matters on the one path where the app has to
 * delete the cache itself: an un-adoptable old controller, offline, during the
 * deploy window. Deleting only the current name there leaves `v1` in place with
 * the previous household's responses in it, and the worker that is still
 * serving them. So the fallback purges by prefix, not by name, and it keeps
 * working across the next roll without anyone remembering to list the old one.
 */
export const SW_API_CACHE_PREFIX = "household-api-";

/**
 * Posted to the worker when the household context changes, immediately before
 * the API cache is purged (codex review).
 *
 * Deleting the cache does not stop a read that was *already in flight* under
 * the previous household: it resolves afterwards and the worker's fetch handler
 * writes it into a freshly re-created cache, putting the old household's body
 * back where the next failed read will find it. The worker stamps each API
 * request with the epoch current when it started and refuses to cache a
 * response whose epoch has since moved on, so those in-flight replies are
 * dropped instead of stored.
 */
export const SW_CACHE_RESET_MESSAGE = "household-api-cache-reset";

/**
 * The body served at `/sw-cache-names.js`: a classic script that hangs the same
 * values off the worker's global scope. Generated from the constants above
 * rather than written out, so the emitted copy cannot fall behind the module.
 */
export function swCacheNamesScript(): string {
  return [
    `self.SW_CACHE_NAMES = ${JSON.stringify(SW_CACHE_NAMES)};`,
    `self.SW_CACHE_RESET_MESSAGE = ${JSON.stringify(SW_CACHE_RESET_MESSAGE)};`,
    ""
  ].join("\n");
}
